import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useToast, toastErrorMessage } from "../../lib/toast";
import Sheet from "../../components/Sheet";
import { Plus } from "lucide-react";

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const toast = useToast();
  async function reload() { const { data } = await api.get("/admin/users"); setRows(data); }
  useEffect(()=>{ reload(); }, []);
  async function deactivate(u) {
    if (confirm(`Deactivate ${u.name}?`)) {
      try { await api.post(`/admin/users/${u.id}/deactivate`); toast.success(`${u.name} deactivated`); reload(); }
      catch(e){ toast.error(toastErrorMessage(e)); }
    }
  }
  async function activate(u) {
    try { await api.post(`/admin/users/${u.id}/activate`); toast.success(`${u.name} activated`); reload(); }
    catch(e){ toast.error(toastErrorMessage(e)); }
  }
  async function resetPw(u) {
    const p = prompt(`Set new password for ${u.name} (min 8 chars):`);
    if (!p) return;
    try { await api.post(`/admin/users/${u.id}/reset-password`, { new_password: p }); toast.success(`Password reset for ${u.name}`); }
    catch(e) { toast.error(toastErrorMessage(e)); }
  }
  async function del(u) {
    if (!confirm(`Delete ${u.name}? This is permanent.`)) return;
    try {
      // First call: no force → backend checks dependencies.
      const { data } = await api.delete(`/admin/users/${u.id}`);
      if (data.ok) {
        toast.success(`${u.name} deleted`);
        reload();
        return;
      }
      if (data.has_dependencies) {
        const c = data.counts;
        const summary =
          `${u.name} owns:\n` +
          `  • ${c.activities_created} activity entr${c.activities_created===1?"y":"ies"}\n` +
          `  • ${c.tasks_assigned} task${c.tasks_assigned===1?"":"s"} assigned\n` +
          `  • ${c.quotes_created} quote${c.quotes_created===1?"":"s"}\n` +
          `  • ${c.companies_created} compan${c.companies_created===1?"y":"ies"} created\n` +
          `  • ${c.companies_assigned} compan${c.companies_assigned===1?"y":"ies"} assigned\n\n` +
          `Deleting will remove attribution ("Created by [deleted user]"). We recommend Deactivate instead so history is preserved.\n\n` +
          `Type FORCE to delete anyway:`;
        const ans = prompt(summary);
        if (ans !== "FORCE") { toast.info("Delete cancelled"); return; }
        await api.delete(`/admin/users/${u.id}?force=true`);
        toast.success(`${u.name} deleted (with attribution removed)`);
        reload();
      }
    } catch(e) {
      toast.error(toastErrorMessage(e));
    }
  }
  const activeRows = rows.filter(u => u.active);
  const deactivatedRows = rows.filter(u => !u.active);

  function renderRow(u) {
    return (
      <tr key={u.id} className="border-t" data-testid="user-row">
        <td className="px-3 py-2 font-medium">{u.name}</td>
        <td className="px-3 py-2">{u.email}</td>
        <td className="px-3 py-2"><span className={`pill ${u.role === "admin" ? "pill-green" : "pill-blue"}`}>{u.role}</span></td>
        <td className="px-3 py-2">{u.active ? "Active" : <span className="pill pill-red">Deactivated</span>}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <button className="text-xs text-gray-600 hover:text-black mr-3" onClick={()=>resetPw(u)}
                  data-testid="btn-reset-pw">Reset password</button>
          {u.active
            ? <button className="text-xs text-amber-600 mr-3" onClick={()=>deactivate(u)} data-testid="btn-deactivate">Deactivate</button>
            : <button className="text-xs text-green-700 mr-3" onClick={()=>activate(u)} data-testid="btn-activate">Activate</button>}
          <button className="text-xs text-red-600" onClick={()=>del(u)} data-testid="btn-delete-user">Delete</button>
        </td>
      </tr>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Users</h1>
        <button className="cesco-btn-primary flex items-center gap-2" onClick={()=>setShow(true)}
                data-testid="btn-new-user"><Plus size={16}/> Add user</button>
      </div>

      {/* Active users */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wider">
          Active - {activeRows.length}
        </h2>
        <div className="cesco-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600"><tr>
              <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Status</th>
              <th></th>
            </tr></thead>
            <tbody data-testid="active-users-body">
              {activeRows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No active users</td></tr>
              )}
              {activeRows.map(renderRow)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deactivated users - only render the section when there are any */}
      {deactivatedRows.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">
            Deactivated - {deactivatedRows.length}
          </h2>
          <div className="cesco-card overflow-x-auto opacity-90">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Status</th>
                <th></th>
              </tr></thead>
              <tbody data-testid="deactivated-users-body">
                {deactivatedRows.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {show && <NewUserForm onClose={()=>setShow(false)} onSaved={reload} toast={toast}/>}
    </div>
  );
}

function NewUserForm({ onClose, onSaved, toast }) {
  const [f, setF] = useState({ email:"", name:"", role:"sales", password:"" });
  const [err, setErr] = useState("");
  async function save() {
    setErr("");
    if (f.password.length < 8) { setErr("Password must be at least 8 chars"); return; }
    try {
      await api.post("/admin/users", { ...f, email: f.email.trim().toLowerCase() });
      toast.success(`${f.name} created`);
      onSaved(); onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose} title="New user"
      footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                  <button className="cesco-btn-primary" onClick={save} data-testid="user-save">Create</button></div>}>
      <div className="space-y-3">
        <input className="cesco-input" placeholder="Name" value={f.name} onChange={e=>setF({...f, name:e.target.value})} data-testid="user-name"/>
        <input className="cesco-input" placeholder="Email" type="email" value={f.email} onChange={e=>setF({...f, email:e.target.value})} data-testid="user-email"/>
        <select className="cesco-input" value={f.role} onChange={e=>setF({...f, role:e.target.value})} data-testid="user-role">
          <option value="sales">Sales</option><option value="admin">Admin</option>
        </select>
        <input className="cesco-input" placeholder="Temporary password (min 8)" type="text" value={f.password} onChange={e=>setF({...f, password:e.target.value})} data-testid="user-password"/>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}
